/**
 * Revenue → Debtors tab. True debtor analytics straight from
 * QuickBooks Online (read-only): headline AR KPIs, aged debt per
 * client, the late-payer league and invoiced-vs-collected cash
 * timeline. Data is cached server-side for 30 minutes with a manual
 * refresh.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Lock } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip } from "./cfoShared";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtGbp = (v) =>
  v == null
    ? "–"
    : `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtMonth = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", {
    month: "short", year: "2-digit",
  });
};

const AGE_COLS = [
  { key: "current", label: "Current" },
  { key: "b1_30", label: "1–30" },
  { key: "b31_60", label: "31–60" },
  { key: "b61_90", label: "61–90" },
  { key: "b91_plus", label: "91+" },
];

const fmtExact = (v) =>
  v == null
    ? "–"
    : `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DebtorsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    force ? setRefreshing(true) : setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/quickbooks/debtors${force ? "?refresh=1" : ""}`,
      );
      setData(d);
      setError(null);
      if (force) toast.success("Refreshed from QuickBooks.");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load debtor data - is QuickBooks connected?",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || error) {
    return <TabState loading={loading} error={error} testid="debtors-state" />;
  }
  const k = data.kpis || {};

  return (
    <div className="flex flex-col gap-6" data-testid="debtors-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border text-emerald-800 bg-emerald-50 border-emerald-200">
          <Lock className="w-2.5 h-2.5" />
          QuickBooks · read-only
          {data.environment === "sandbox" ? " · sandbox" : ""}
          {data.company_name ? ` · ${data.company_name}` : ""}
        </span>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="debtors-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1B4F8A] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {data.generated_at
            ? `As of ${new Date(data.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
            : "Refresh"}
        </button>
      </div>

      <KpiStrip
        cards={[
          {
            label: "Outstanding AR",
            value: fmtExact(k.total_ar),
            sub: `including VAT · ${k.open_invoices || 0} open invoices`,
            testid: "debtors-kpi-ar",
            info: "ar_vat_split",
          },
          {
            label: "Excl VAT",
            value: fmtExact(k.total_ar_net),
            sub: "the net revenue still to collect",
            testid: "debtors-kpi-net",
          },
          {
            label: "VAT within debtors",
            value: fmtExact(k.total_ar_vat),
            sub: "output VAT owed to HMRC",
            testid: "debtors-kpi-vat",
          },
          {
            label: "Overdue",
            value: fmtExact(k.overdue_ar),
            sub: `${k.overdue_pct || 0}% of outstanding`,
            tone: k.overdue_ar > 0 ? "text-rose-600" : "text-slate-900",
            testid: "debtors-kpi-overdue",
          },
          {
            label: "DSO",
            value: k.dso != null ? `${k.dso}d` : "–",
            sub: "AR ÷ trailing 12m sales × 365",
            testid: "debtors-kpi-dso",
          },
          {
            label: "Avg days to pay",
            value: k.avg_days_to_pay != null ? `${k.avg_days_to_pay}d` : "–",
            sub: k.median_days_to_pay != null ? `median ${k.median_days_to_pay}d` : "",
            testid: "debtors-kpi-dtp",
          },
        ]}
      />

      <Card testid="debtors-aged-card">
        <PanelTitle>Aged debt by client</PanelTitle>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-200">
                <th className="py-1.5 pr-2 text-left font-medium">Client</th>
                {AGE_COLS.map((c) => (
                  <th key={c.key} className="py-1.5 px-2 text-right font-medium">{c.label}</th>
                ))}
                <th className="py-1.5 pl-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.aged.map((r) => (
                <tr key={r.client} className="border-b border-stone-50 last:border-0" data-testid={`debtors-aged-row-${r.client}`}>
                  <td className="py-1.5 pr-2 font-medium text-slate-800">{r.client}</td>
                  {AGE_COLS.map((c) => (
                    <td
                      key={c.key}
                      className={`py-1.5 px-2 text-right font-mono tabular-nums ${
                        r[c.key] > 0
                          ? c.key === "b91_plus"
                            ? "text-rose-600 font-semibold"
                            : c.key === "current"
                            ? "text-slate-700"
                            : "text-amber-700"
                          : "text-stone-300"
                      }`}
                    >
                      {r[c.key] > 0 ? fmtGbpShort(r[c.key]) : "–"}
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right font-mono tabular-nums font-semibold text-slate-900">
                    {fmtGbp(r.total)}
                  </td>
                </tr>
              ))}
              {data.aged.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center text-stone-400">No outstanding invoices 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card testid="debtors-league-card">
        <PanelTitle>Late-payer league · paid invoices</PanelTitle>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-200">
                <th className="py-1.5 pr-2 text-left font-medium">Client</th>
                <th className="py-1.5 px-2 text-right font-medium">Paid invoices</th>
                <th className="py-1.5 px-2 text-right font-medium">Avg days to pay</th>
                <th className="py-1.5 px-2 text-right font-medium">Median</th>
                <th className="py-1.5 px-2 text-right font-medium">% paid late</th>
                <th className="py-1.5 pl-2 text-right font-medium">Total paid</th>
              </tr>
            </thead>
            <tbody>
              {data.league.map((r) => (
                <tr key={r.client} className="border-b border-stone-50 last:border-0">
                  <td className="py-1.5 pr-2 font-medium text-slate-800">{r.client}</td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">{r.paid_invoices}</td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${r.avg_days_to_pay > 45 ? "text-rose-600 font-semibold" : r.avg_days_to_pay > 30 ? "text-amber-700" : "text-emerald-700"}`}>
                    {r.avg_days_to_pay}d
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums text-stone-600">{r.median_days_to_pay}d</td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${r.pct_paid_late >= 50 ? "text-rose-600" : "text-stone-600"}`}>
                    {r.pct_paid_late}%
                  </td>
                  <td className="py-1.5 pl-2 text-right font-mono tabular-nums text-slate-900">{fmtGbpShort(r.total_paid)}</td>
                </tr>
              ))}
              {data.league.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-stone-400">No fully-paid invoices with linked payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card testid="debtors-timeline-card">
        <PanelTitle>Invoiced vs collected · last 12 months</PanelTitle>
        <div className="h-56 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.timeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                labelFormatter={fmtMonth}
                formatter={(v, n) => [fmtGbp(v), n === "invoiced" ? "Invoiced" : "Collected"]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === "invoiced" ? "Invoiced" : "Collected")} />
              <Bar dataKey="invoiced" fill="#1B4F8A" maxBarSize={22} radius={[3, 3, 0, 0]} />
              <Bar dataKey="collected" fill="#0D9488" maxBarSize={22} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
